import React, { useState, useEffect } from "react";
import { X, ShoppingBag, Download, Minus, Plus } from "lucide-react";
import { getSizes } from "../data/worldData";

const TONE = {
  blue: "#243B52",
  ochre: "#CF9026",
  oxblood: "#4E0E18",
};

export default function MerchModal({ item, onClose, onAdd }) {
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setSize(null);
    setQty(1);
  }, [item?.id]);

  if (!item) return null;
  const bg = TONE[item.tone] || "#4E0E18";
  const sizes = getSizes(item);
  const needsSize = sizes.length > 0;
  const canAdd = !needsSize || !!size;

  const handleAdd = () => {
    if (!canAdd) return;
    onAdd?.(item, { size, qty });
    onClose();
  };

  return (
    <div
      data-testid="merch-modal"
      className="fixed inset-0 z-[95] flex items-center justify-center p-4 grain"
      style={{ background: "rgba(21,16,14,0.82)" }}
      onClick={onClose}
    >
      <div className="relative w-full max-w-md bg-[#ECE7DB] text-[#15100E] edge animate-fade-up max-h-[92vh] overflow-y-auto bg-scroll" onClick={(e) => e.stopPropagation()}>
        <button data-testid="merch-modal-close" onClick={onClose} className="absolute top-3 right-3 z-10 h-9 w-9 bg-[#15100E] text-[#ECE7DB] flex items-center justify-center hover:bg-[#4E0E18] transition-colors">
          <X size={18} />
        </button>
        <div className="relative aspect-square border-b-2 border-[#15100E]" style={{ background: bg }}>
          <img src={item.img} alt={item.name} className="w-full h-full object-cover" />
          <span className="absolute bottom-3 left-4 font-tech text-[#ECE7DB] text-xs tracking-widest">{item.note}</span>
        </div>
        <div className="p-6">
          <div className="flex items-end justify-between gap-4">
            <h3 className="font-display font-extrabold stretch-text text-3xl sm:text-4xl leading-[0.9]">{item.name}</h3>
            <span className="font-tech text-[#7A1623] text-xl shrink-0">{item.price}</span>
          </div>
          <p className="font-body text-[#15100E]/65 text-sm mt-3">{item.desc}</p>
          {item.digital && (
            <p className="font-tech text-[#243B52] text-[11px] tracking-widest mt-3 flex items-center gap-2">
              <Download size={14} /> INSTANT DIGITAL DOWNLOAD AFTER CHECKOUT
            </p>
          )}

          {needsSize && (
            <div className="mt-5">
              <p className="font-tech text-[#15100E]/60 text-[11px] tracking-[0.25em] mb-2">SELECT SIZE</p>
              <div className="flex flex-wrap gap-2">
                {sizes.map((s) => (
                  <button
                    key={s}
                    data-testid={`merch-size-${s}`}
                    onClick={() => setSize(s)}
                    className={`min-w-[46px] h-10 px-3 border-2 border-[#15100E] font-tech text-sm tracking-widest transition-colors ${size === s ? "bg-[#15100E] text-[#ECE7DB]" : "bg-transparent text-[#15100E] hover:bg-[#CF9026]"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-5">
            <p className="font-tech text-[#15100E]/60 text-[11px] tracking-[0.25em] mb-2">QUANTITY</p>
            <div className="flex items-center gap-3">
              <button data-testid="merch-qty-dec" onClick={() => setQty((q) => Math.max(1, q - 1))} className="h-10 w-10 border-2 border-[#15100E] flex items-center justify-center hover:bg-[#CF9026] transition-colors">
                <Minus size={16} />
              </button>
              <span data-testid="merch-qty" className="font-display font-extrabold text-2xl w-10 text-center">{qty}</span>
              <button data-testid="merch-qty-inc" onClick={() => setQty((q) => Math.min(99, q + 1))} className="h-10 w-10 border-2 border-[#15100E] flex items-center justify-center hover:bg-[#CF9026] transition-colors">
                <Plus size={16} />
              </button>
            </div>
          </div>

          <button
            data-testid="merch-add-cart"
            onClick={handleAdd}
            disabled={!canAdd}
            className="mt-6 w-full font-tech tracking-[0.2em] text-sm text-[#ECE7DB] bg-[#15100E] py-3.5 flex items-center justify-center gap-2 hover:bg-[#4E0E18] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ShoppingBag size={16} /> {canAdd ? `ADD TO CART · ${item.price}` : "SELECT A SIZE"}
          </button>
          <p className="font-tech text-center text-[#15100E]/40 text-[11px] mt-3 tracking-wide">SECURE STRIPE CHECKOUT</p>
        </div>
      </div>
    </div>
  );
}
